import React from 'react';
import { Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Text from '../Text';
import SectionHeader from '../SectionHeader';
import ScrollReveal from '../animated/ScrollReveal';
import MagneticButton from '../animated/MagneticButton';
import { colors, gradients } from '../../theme/designTokens';
import { insights } from '../../data/insights';
import { caseStudies } from '../../data/caseStudies';

export const InsightsTeaser = () => {
  const navigate = useNavigate();
  const featured = insights.slice(0, 3);

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.white }}>
      <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
        <ScrollReveal>
          <SectionHeader
            eyebrow="Thought Leadership"
            title="Ideas that build trust."
            highlightWords={['trust']}
            subtitle="Perspectives, research, and frameworks for leaders who engineer resilience."
          />
        </ScrollReveal>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2.5,
          }}
        >
          {featured.map((item) => (
            <Box
              key={item.slug}
              sx={{
                p: 3,
                borderTop: `2px solid ${colors.navy}`,
                borderLeft: `1px solid ${colors.borderLight}`,
                borderRight: `1px solid ${colors.borderLight}`,
                borderBottom: `1px solid ${colors.borderLight}`,
              }}
            >
              <Text variant="eyebrow" sx={{ color: colors.slate, mb: 1.5, display: 'block' }}>
                {item.type}
              </Text>
              <Text variant="h4" color="primary" sx={{ mb: 1.5 }}>
                {item.title}
              </Text>
              <Text variant="bodySm" color="secondary">
                {item.excerpt}
              </Text>
            </Box>
          ))}
        </Box>
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <MagneticButton variant="ghost" onClick={() => navigate('/insights')}>
            View All Insights
          </MagneticButton>
        </Box>
      </Box>
    </Box>
  );
};

const STAGES = [
  { key: 'before', label: 'Before' },
  { key: 'problem', label: 'Problem' },
  { key: 'solution', label: 'Solution' },
  { key: 'outcome', label: 'Outcome' },
];

export const CaseStudiesSection = () => (
  <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, md: 12 }, background: colors.offWhite }}>
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <ScrollReveal>
        <SectionHeader
          eyebrow="Case Studies"
          title="Don't talk. Show."
          highlightWords={['Show']}
          subtitle="Before → Problem → Solution → Outcome. Measurable resilience in practice."
        />
      </ScrollReveal>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 3, md: 4 } }}>
        {caseStudies.map((cs, index) => (
          <ScrollReveal key={cs.id} delay={index * 0.05}>
            <Box
              sx={{
                background: colors.white,
                border: `1px solid ${colors.borderLight}`,
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  px: { xs: 3, md: 4 },
                  py: { xs: 2.5, md: 3 },
                  borderBottom: `1px solid ${colors.borderLight}`,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { sm: 'baseline' },
                  gap: { xs: 1, sm: 2 },
                }}
              >
                <Text
                  variant="eyebrow"
                  sx={{
                    color: colors.slate,
                    display: 'block',
                    flexShrink: 0,
                  }}
                >
                  {cs.industry}
                </Text>
                <Text variant="h3" color="primary" sx={{ display: 'block' }}>
                  {cs.title}
                </Text>
              </Box>

              {/* Journey stages */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: '1fr 1fr',
                    md: 'repeat(4, 1fr)',
                  },
                  px: { xs: 0, md: 0 },
                }}
              >
                {STAGES.map((stage, i) => (
                  <Box
                    key={stage.key}
                    sx={{
                      px: { xs: 3, md: 3.5 },
                      py: { xs: 2.5, md: 3.5 },
                      borderRight: {
                        xs: 'none',
                        sm: i % 2 === 0 ? `1px solid ${colors.borderLight}` : 'none',
                        md: i < STAGES.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
                      },
                      borderBottom: {
                        xs: i < STAGES.length - 1 ? `1px solid ${colors.borderLight}` : 'none',
                        sm: i < 2 ? `1px solid ${colors.borderLight}` : 'none',
                        md: 'none',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Text
                        variant="caption"
                        sx={{
                          color: colors.silverDark,
                          fontWeight: 600,
                          letterSpacing: '0.08em',
                        }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </Text>
                      <Text variant="label" sx={{ color: colors.navy, display: 'block' }}>
                        {stage.label}
                      </Text>
                    </Box>
                    <Text variant="bodySm" color="secondary" sx={{ display: 'block' }}>
                      {cs[stage.key]}
                    </Text>
                  </Box>
                ))}
              </Box>

              {/* Metrics strip — aligned 3-column grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                  background: gradients.sectionDark,
                  borderTop: `1px solid ${colors.borderLight}`,
                }}
              >
                {cs.metrics.map((m, i) => (
                  <Box
                    key={m.label}
                    sx={{
                      px: { xs: 3, md: 3.5 },
                      py: { xs: 2.5, md: 3 },
                      textAlign: { xs: 'left', sm: 'center' },
                      borderRight: {
                        xs: 'none',
                        sm: i < cs.metrics.length - 1 ? `1px solid ${colors.glassBorder}` : 'none',
                      },
                      borderBottom: {
                        xs: i < cs.metrics.length - 1 ? `1px solid ${colors.glassBorder}` : 'none',
                        sm: 'none',
                      },
                    }}
                  >
                    <Text
                      variant="stat"
                      sx={{ color: colors.ochreLight, display: 'block', mb: 0.75 }}
                    >
                      {m.value}
                    </Text>
                    <Text
                      variant="caption"
                      sx={{
                        color: colors.silver,
                        display: 'block',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                      }}
                    >
                      {m.label}
                    </Text>
                  </Box>
                ))}
              </Box>
            </Box>
          </ScrollReveal>
        ))}
      </Box>
    </Box>
  </Box>
);

export default InsightsTeaser;
